import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Button, Input, Separator, Spinner } from '@treams/treams-ui';

import PopoverConfirm from '../../packages/kakapo-ui-kit/components/popovers/PopoverConfirm';
import { FieldLabels } from '../ProductConfiguration';
import ProductConfigurationFieldValue from './ConfigurationFieldValue';

const Element = styled.div`
	&:hover {
		position: relative;
	}
`;

export default function ConfigurationField(props: {
	connectorType?: graphql.ConnectorType;
	translate?: (key: string) => FieldLabels | undefined;
	field: graphql.ConnectorConfigField;
	onSave: (input: Partial<graphql.UpdateConnectorConfigFieldInput>) => Promise<void>;
}) {
	const { t } = useTranslation();

	const [editing, setEditing] = useState(false);
	const [loading, setLoading] = useState(false);
	const saving = useRef(false);

	const [change, setChange] = useState<
		Partial<graphql.UpdateConnectorConfigFieldInput> | undefined
	>();

	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const usesAutoSave =
		props.field.type === graphql.ConnectorConfigFieldType.Oauth2 ||
		props.field.type === graphql.ConnectorConfigFieldType.Certificate;

	const needsConfirmation =
		props.field.isDangerous &&
		!(props.field as graphql.ConnectorConfigFieldBoolean & { boolean: boolean }).boolean;

	const changeValue = async (newValue) => {
		setChange(newValue);
		if (!usesAutoSave) {
			return;
		}

		if (saving.current) {
			return;
		}

		saving.current = true;
		setLoading(true);

		try {
			await props.onSave(newValue);
		} finally {
			saving.current = false;
			setLoading(false);
		}
	};

	const confirmAndSubmit = async (confirmed: boolean) => {
		if (!confirmed) {
			return setShowConfirm(false);
		}

		await props.onSave(change!);
		setEditing(false);
		setChange(undefined);
		setShowConfirm(false);
	};

	const submit = async () => {
		await props.onSave(change!);
		setEditing(false);
		setChange(undefined);
	};

	return (
		<>
			<Flex alignItems="flex-start">
				{'isSecret' in props.field &&
				props.field.isSecret &&
				props.field.isConfigured &&
				!editing ? (
					<Flex alignItems="center">
						<Input value="******" onChange={() => {}} disabled />
						<Button
							type="secondary"
							ml={2}
							title={t('connectors.settings.configuration.changeValue')}
							onClick={() => setEditing(true)}
						/>
					</Flex>
				) : (
					<ProductConfigurationFieldValue
						connectorType={props.connectorType}
						field={props.field}
						change={change}
						onChange={(newValue) => changeValue(newValue)}
						autoFocus={editing}
					/>
				)}
				{loading && <Spinner ml={2} />}
			</Flex>

			{!usesAutoSave && change && (
				<Box mt={3} mx={-3}>
					<Separator />

					<Flex mt={3} mx={3}>
						<Element>
							<Button
								type="primary"
								title={t('common.save')}
								onClick={() => (needsConfirmation ? setShowConfirm(true) : submit())}
							/>

							{showConfirm && (
								<PopoverConfirm
									title={
										props.translate ? t(props.translate(props.field.key)?.confirmTitle ?? '') : ''
									}
									description={
										props.translate
											? t(props.translate(props.field.key)?.confirmDescription ?? '')
											: ''
									}
									confirmButtonTitle={t('common.confirm')}
									cancelButtonTitle={t('common.cancel')}
									actionType="delete"
									onConfirm={(confirmed) => confirmAndSubmit(confirmed)}
								/>
							)}
						</Element>
					</Flex>
				</Box>
			)}
		</>
	);
}
